import { useState } from "react";
import { Navigate, useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, ArrowLeft } from "lucide-react";
import { Logo } from "@/components/Logo";
import { useAuth, formatApiError } from "@/lib/auth";
import { EASE } from "@/components/Reveal";

export default function AdminLogin() {
  const { user, login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  if (user) return <Navigate to="/admin" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      await login(email, password);
      nav("/admin", { replace: true });
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="admin-login-page" className="relative flex min-h-screen items-center justify-center overflow-hidden bg-brand-navy px-6 text-white">
      <div className="hero-grid absolute inset-0" />
      <div className="absolute -left-40 top-1/3 h-[520px] w-[520px] rounded-full bg-brand-indigo/40 blur-[140px]" />
      <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, ease: EASE }} className="glass relative w-full max-w-md rounded-[2rem] p-8 sm:p-10">
        <Logo dark testId="admin-login-logo" />
        <h1 className="mt-10 font-display text-3xl font-bold tracking-tight">Enquiry dashboard</h1>
        <p className="mt-2 text-sm text-white/55">Private area for the Malansh team.</p>
        <form onSubmit={submit} className="mt-8 space-y-5">
          <label className="block">
            <span className="mb-2 block font-mono text-[10px] uppercase tracking-[0.25em] text-white/50">Email</span>
            <input data-testid="admin-login-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className="field" placeholder="you@malansh.in" />
          </label>
          <label className="block">
            <span className="mb-2 block font-mono text-[10px] uppercase tracking-[0.25em] text-white/50">Password</span>
            <input data-testid="admin-login-password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} className="field" placeholder="••••••••" />
          </label>
          {error && <p data-testid="admin-login-error" className="rounded-xl border border-red-400/30 bg-red-500/10 px-4 py-3 text-sm text-red-200">{error}</p>}
          <button data-testid="admin-login-submit" type="submit" disabled={busy} className="btn-primary w-full justify-center disabled:opacity-60">
            <span>{busy ? "Signing in…" : "Sign in"}</span>
            <ArrowRight className="h-4 w-4" />
          </button>
        </form>
        <Link data-testid="admin-back-home" to="/" className="mt-8 inline-flex items-center gap-2 text-sm text-white/50 hover:text-white">
          <ArrowLeft className="h-4 w-4" /> Back to website
        </Link>
      </motion.div>
    </div>
  );
}
